/**
 * Which values a role may see (#1115). Mirrors the API's role stripping (hard rule 5): `money` is MoneyField
 * (manager, cover, billing), `cost` is CostField (manager, billing), `manager` is manager only. The API already drops
 * the values; the web uses this to hide the whole column or tile instead of rendering an empty cell.
 */
import { useCallback } from 'react'
import type { Metric } from '../../lib/api/generated'
import { useMe } from '../auth/session'

export type Visibility = Metric['visibility'] & string
export type CanSee = (visibility: Visibility | null | undefined) => boolean

const MONEY = new Set(['manager', 'cover', 'billing'])
const COST = new Set(['manager', 'billing'])

export function canSeeFor(role: string | null | undefined, visibility: Visibility | null | undefined): boolean {
  if (!visibility || visibility === 'all') return true
  if (!role) return false
  if (visibility === 'money') return MONEY.has(role)
  if (visibility === 'cost') return COST.has(role)
  return role === 'manager'
}

/** Licence margin is cost data: manager and billing (the API's COST_READERS). */
export const canSeeLicences = (role: string | null | undefined): boolean => canSeeFor(role, 'cost')

export function useCanSee(): CanSee {
  const role = useMe()?.role
  return useCallback((visibility) => canSeeFor(role, visibility), [role])
}
